export interface ReadingPosition {
    bookId: string;
    chapter: number;
    verse?: number;
    pageIndex?: number; // fallback if we support resuming page
    timestamp: number;
}

export interface Bookmark {
    id: string;
    bookId: string;
    chapter: number;
    verse?: number;
    label?: string;
    createdAt: number;
}

const STORAGE_KEY_POS = 'bible_last_read'; // Legacy
const STORAGE_KEY_BOOKMARKS = 'bible_bookmarks';
const STORAGE_KEY_HISTORY = 'bible_read_history';

// Key format: bible_pos_CORPUSID_BOOKID
const getPosKey = (corpusId: string, bookId: string) => `bible_pos_${corpusId}_${bookId}`;

// Consolidated Store Key
const BIG_KEY = 'verseflip.readerProgress.v1';

export interface ReadingPosition {
    corpusId: string;
    bookId: string;
    chapter: number;
    verse?: number;
    pageIndex?: number;
    timestamp: number;
}

export const saveLastRead = (pos: ReadingPosition) => {
    try {
        // Strategy: Save to individual key AND big store for safety/migration
        const key = getPosKey(pos.corpusId, pos.bookId);
        localStorage.setItem(key, JSON.stringify(pos));

        // Unified Store update
        const rawBig = localStorage.getItem(BIG_KEY);
        const store = rawBig ? JSON.parse(rawBig) : {};
        store[`${pos.corpusId}:${pos.bookId}`] = pos;
        localStorage.setItem(BIG_KEY, JSON.stringify(store));

        addToHistory(pos);

        console.log(`[SmartReader] Progress saved: ${pos.bookId} Ch${pos.chapter} Page${pos.pageIndex} (${new Date().toLocaleTimeString()})`);
    } catch (e) {
        console.warn('Failed to save pos', e);
    }
};

export const loadLastRead = (corpusId: string, bookId: string): ReadingPosition | null => {
    try {
        // Try unified first
        const rawBig = localStorage.getItem(BIG_KEY);
        if (rawBig) {
            const store = JSON.parse(rawBig);
            const val = store[`${corpusId}:${bookId}`];
            if (val) {
                console.log(`[SmartReader] Progress loaded (unified): ${bookId} Ch${val.chapter} Page${val.pageIndex}`);
                return val;
            }
        }

        // Fallback to legacy individual key
        const key = getPosKey(corpusId, bookId);
        const raw = localStorage.getItem(key);
        if (raw) {
            console.log(`[SmartReader] Progress loaded (legacy): ${bookId}`);
            return JSON.parse(raw);
        }
        return null;
    } catch {
        return null;
    }
};

export const clearLastRead = (corpusId: string, bookId: string) => {
    try {
        const key = getPosKey(corpusId, bookId);
        localStorage.removeItem(key);

        const rawBig = localStorage.getItem(BIG_KEY);
        if (rawBig) {
            const store = JSON.parse(rawBig);
            delete store[`${corpusId}:${bookId}`];
            localStorage.setItem(BIG_KEY, JSON.stringify(store));
        }
    } catch { }
};

// Legacy fallback
export const loadLegacyLastRead = (): ReadingPosition | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_POS);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

export const saveBookmark = (b: Bookmark) => {
    try {
        const list = loadBookmarks();
        list.push(b);
        localStorage.setItem(STORAGE_KEY_BOOKMARKS, JSON.stringify(list));
    } catch (e) {
        console.error('Failed to save bookmark', e);
    }
};

export const loadBookmarks = (): Bookmark[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_BOOKMARKS);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

export const deleteBookmark = (id: string) => {
    try {
        const list = loadBookmarks().filter(b => b.id !== id);
        localStorage.setItem(STORAGE_KEY_BOOKMARKS, JSON.stringify(list));
    } catch (e) {
        console.error(e);
    }
};

// History Ring Buffer (Size 20)
const HISTORY_SIZE = 20;

const addToHistory = (pos: ReadingPosition) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_HISTORY);
        let list: ReadingPosition[] = raw ? JSON.parse(raw) : [];

        // Simple dedupe: if last entry is same book/chapter, just update timestamp
        const last = list[0];
        if (last && last.bookId === pos.bookId && last.chapter === pos.chapter) {
            list[0] = pos;
        } else {
            list.unshift(pos);
        }

        if (list.length > HISTORY_SIZE) {
            list = list.slice(0, HISTORY_SIZE);
        }
        localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(list));
    } catch (e) {
        console.error('Failed to update history', e);
    }
};

export const loadHistory = (): ReadingPosition[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_HISTORY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

export const triggerHaptic = () => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
            navigator.vibrate(5);
        } catch { /* ignore */ }
    }
};
